import React, { useState, useEffect, useCallback, useRef } from 'react'
import { usePrintStore, type PrintMethod } from '@renderer/stores/printStore'
import { useConfigStore } from '@renderer/stores/configState'
import Button from '@renderer/components/Button'
import { useTranslation } from 'react-i18next'
import { FaUpload, FaPrint, FaDownload } from 'react-icons/fa'
import { useDeviceManager, useDesignLayout, useSessionLayout } from '@renderer/hooks'
import cogoToast from '@successtar/cogo-toast'
import { PrintUtils, PrinterCaps } from '@renderer/utils/printUtils'
import {
  capabilities,
  getHostAppVersion,
  getPreloadApi,
  getTunnelStatus,
  hasElectronPreload,
} from '@renderer/lib/runtime'
import { isInTauriShell } from '@renderer/lib/runtime'
import { logPrintEvent } from '@renderer/utils/printEventLogger'
import { FrameSelectionModal } from './FrameSelectionModal'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS } from '@renderer/utils/printResultMinDisplay'

interface PrinterSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  isElectron?: boolean
}

interface MarginSettings {
  top: number
  bottom: number
  left: number
  right: number
}

type PrinterTab = 'default' | 'secondary' | 'tertiary'

export const PrinterSettingsModal: React.FC<PrinterSettingsModalProps> = ({
  isOpen,
  onClose,
  isElectron = hasElectronPreload()
}) => {
  const isNativeShell = isElectron || isInTauriShell()
  const { t } = useTranslation()
  const { isCompact: isViewportCompact } = useSessionLayout()
  const { isCompact: printerDesignCompact, isPortrait: printerDesignPortrait } = useDesignLayout()
  const printerSheetDense = (capabilities.isMobile || isViewportCompact) && printerDesignCompact && printerDesignPortrait
  const {
    settings,
    updateSettings,
    secondaryPrinter,
    setSecondaryPrinter,
    tertiaryPrinter,
    setTertiaryPrinter,
    selectedPrinter: printStoreSelectedPrinter,
    setSelectedPrinter: updateGlobalSelectedPrinter
  } = usePrintStore()
  const printResultMinDisplaySeconds = useSettingsStore((s) => s.printResultMinDisplaySeconds)
  const setPrintResultMinDisplaySeconds = useSettingsStore((s) => s.setPrintResultMinDisplaySeconds)
  const { frames, getFrame } = useConfigStore()
  const { devices } = useDeviceManager()
  const [activeTab, setActiveTab] = useState<PrinterTab>('default')
  const [tempSettings, setTempSettings] = useState(settings)
  const [tempSecondarySettings, setTempSecondarySettings] = useState({
    ...settings,
    ...(secondaryPrinter?.settings || {}),
    orientation: secondaryPrinter?.settings?.orientation || 'landscape',
    pageSize: {
      height: secondaryPrinter?.settings?.pageSize?.height || 105000,
      width: secondaryPrinter?.settings?.pageSize?.width || 156100
    },
    scaleFactor: secondaryPrinter?.settings?.scaleFactor || 100
  })
  const [tempTertiarySettings, setTempTertiarySettings] = useState({
    ...settings,
    ...(tertiaryPrinter?.settings || {}),
    orientation: tertiaryPrinter?.settings?.orientation || 'landscape',
    pageSize: {
      height: tertiaryPrinter?.settings?.pageSize?.height || 105000,
      width: tertiaryPrinter?.settings?.pageSize?.width || 156100
    },
    scaleFactor: tertiaryPrinter?.settings?.scaleFactor || 100
  })
  const [marginType, setMarginType] = useState<'default' | 'none' | 'printableArea' | 'custom'>(
    settings.margins?.marginType || 'default'
  )
  const [secondaryMarginType, setSecondaryMarginType] = useState<
    'default' | 'none' | 'printableArea' | 'custom'
  >(secondaryPrinter?.settings?.margins?.marginType || 'default')
  const [tertiaryMarginType, setTertiaryMarginType] = useState<
    'default' | 'none' | 'printableArea' | 'custom'
  >(tertiaryPrinter?.settings?.margins?.marginType || 'default')
  const [margins, setMargins] = useState<MarginSettings>({
    top: settings.margins?.top || 0,
    bottom: settings.margins?.bottom || 0,
    left: settings.margins?.left || 0,
    right: settings.margins?.right || 0
  })
  const [secondaryMargins, setSecondaryMargins] = useState<MarginSettings>({
    top: secondaryPrinter?.settings?.margins?.top || 0,
    bottom: secondaryPrinter?.settings?.margins?.bottom || 0,
    left: secondaryPrinter?.settings?.margins?.left || 0,
    right: secondaryPrinter?.settings?.margins?.right || 0
  })
  const [tertiaryMargins, setTertiaryMargins] = useState<MarginSettings>({
    top: tertiaryPrinter?.settings?.margins?.top || 0,
    bottom: tertiaryPrinter?.settings?.margins?.bottom || 0,
    left: tertiaryPrinter?.settings?.margins?.left || 0,
    right: tertiaryPrinter?.settings?.margins?.right || 0
  })
  const [positionShift, setPositionShift] = useState({
    top: settings.printPositionShift?.top || 0,
    bottom: settings.printPositionShift?.bottom ?? -0.5,
    left: settings.printPositionShift?.left || 0,
    right: settings.printPositionShift?.right || 0
  })
  const [secondaryPositionShift, setSecondaryPositionShift] = useState({
    top: secondaryPrinter?.settings?.printPositionShift?.top || 0,
    bottom: secondaryPrinter?.settings?.printPositionShift?.bottom ?? -0.5,
    left: secondaryPrinter?.settings?.printPositionShift?.left || 0,
    right: secondaryPrinter?.settings?.printPositionShift?.right || 0
  })
  const [tertiaryPositionShift, setTertiaryPositionShift] = useState({
    top: tertiaryPrinter?.settings?.printPositionShift?.top || 0,
    bottom: tertiaryPrinter?.settings?.printPositionShift?.bottom ?? -0.5,
    left: tertiaryPrinter?.settings?.printPositionShift?.left || 0,
    right: tertiaryPrinter?.settings?.printPositionShift?.right || 0
  })
  const [dpi, setDpi] = useState({
    horizontal: settings.dpi?.horizontal || 600,
    vertical: settings.dpi?.vertical || 600
  })
  const [secondaryDpi, setSecondaryDpi] = useState({
    horizontal: secondaryPrinter?.settings?.dpi?.horizontal || 600,
    vertical: secondaryPrinter?.settings?.dpi?.vertical || 600
  })
  const [tertiaryDpi, setTertiaryDpi] = useState({
    horizontal: tertiaryPrinter?.settings?.dpi?.horizontal || 600,
    vertical: tertiaryPrinter?.settings?.dpi?.vertical || 600
  })
  const [scaleFactor, setScaleFactor] = useState(settings.scaleFactor || 100)
  const [secondaryScaleFactor, setSecondaryScaleFactor] = useState(
    secondaryPrinter?.settings?.scaleFactor || 100
  )
  const [tertiaryScaleFactor, setTertiaryScaleFactor] = useState(
    tertiaryPrinter?.settings?.scaleFactor || 100
  )
  const [printBackground, setPrintBackground] = useState(settings.printBackground ?? true)
  const [secondaryPrintBackground, setSecondaryPrintBackground] = useState(
    secondaryPrinter?.settings?.printBackground ?? true
  )
  const [tertiaryPrintBackground, setTertiaryPrintBackground] = useState(
    tertiaryPrinter?.settings?.printBackground ?? true
  )
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [selectedPrinter, setSelectedPrinter] = useState<string>(printStoreSelectedPrinter || '')
  const [selectedSecondaryPrinter, setSelectedSecondaryPrinter] = useState<string>(
    secondaryPrinter?.printerName || ''
  )
  const [selectedTertiaryPrinter, setSelectedTertiaryPrinter] = useState<string>(
    tertiaryPrinter?.printerName || ''
  )
  const [copies, setCopies] = useState(1)
  const [isDragging, setIsDragging] = useState(false)
  const [isFrameSelectionOpen, setIsFrameSelectionOpen] = useState(false)
  const [frameSelectionPrinterType, setFrameSelectionPrinterType] = useState<
    'secondary' | 'tertiary'
  >('secondary')
  const [selectedFrameId, setSelectedFrameId] = useState<string>('')
  const [printResolutionScale, setPrintResolutionScale] = useState(
    settings.printResolutionScale || 1
  )
  // Refs to store previous margin and DPI values when SumatraPDF mode is enabled
  const prevMarginTypeRef = useRef<'default' | 'none' | 'printableArea' | 'custom'>('default')
  const prevSecondaryMarginTypeRef = useRef<'default' | 'none' | 'printableArea' | 'custom'>(
    'default'
  )
  const prevTertiaryMarginTypeRef = useRef<'default' | 'none' | 'printableArea' | 'custom'>(
    'default'
  )
  const prevDpiRef = useRef<{ horizontal: number; vertical: number }>({
    horizontal: 600,
    vertical: 600
  })
  const prevSecondaryDpiRef = useRef<{ horizontal: number; vertical: number }>({
    horizontal: 600,
    vertical: 600
  })
  const prevTertiaryDpiRef = useRef<{ horizontal: number; vertical: number }>({
    horizontal: 600,
    vertical: 600
  })
  const [secondaryPrintResolutionScale, setSecondaryPrintResolutionScale] = useState(
    secondaryPrinter?.settings?.printResolutionScale || 1
  )
  const [tertiaryPrintResolutionScale, setTertiaryPrintResolutionScale] = useState(
    tertiaryPrinter?.settings?.printResolutionScale || 1
  )
  const [tunnelUrl, setTunnelUrl] = useState<string | null>(null)
  const [isTunnelActive, setIsTunnelActive] = useState<boolean>(false)
  const [printerCapabilities, setPrinterCapabilities] = useState<PrinterCaps | null>(null)
  const [secondaryPrinterCapabilities, setSecondaryPrinterCapabilities] =
    useState<PrinterCaps | null>(null)
  const [tertiaryPrinterCapabilities, setTertiaryPrinterCapabilities] =
    useState<PrinterCaps | null>(null)
  const [loadingCapabilities, setLoadingCapabilities] = useState<boolean>(false)

  // Check if printer capabilities API is available (for backward compatibility)
  const [hasPrinterCapsSupport, setHasPrinterCapsSupport] = useState<boolean>(false)
  const [appVersion, setAppVersion] = useState<string | null>(null)

  // Combined preset dropdown state
  const [selectedPresetId, setSelectedPresetId] = useState<string>('custom')
  const [selectedSecondaryPresetId, setSelectedSecondaryPresetId] = useState<string>('custom')
  const [selectedTertiaryPresetId, setSelectedTertiaryPresetId] = useState<string>('custom')
  const [secondaryPurpose, setSecondaryPurpose] = useState<'special-frames' | 'parallel'>(
    secondaryPrinter?.purpose || 'special-frames'
  )
  const [tertiaryPurpose, setTertiaryPurpose] = useState<'special-frames' | 'parallel'>(
    tertiaryPrinter?.purpose || 'special-frames'
  )
  const [combinedOptions, setCombinedOptions] = useState<
    Array<{
      id: string
      name: string | null
      orientation: 'landscape' | 'portrait'
      pageSize: { width: number; height: number }
      dpi: { horizontal: number; vertical: number }
    }>
  >([])
  const [secondaryCombinedOptions, setSecondaryCombinedOptions] = useState<
    Array<{
      id: string
      name: string | null
      orientation: 'landscape' | 'portrait'
      pageSize: { width: number; height: number }
      dpi: { horizontal: number; vertical: number }
    }>
  >([])
  const [tertiaryCombinedOptions, setTertiaryCombinedOptions] = useState<
    Array<{
      id: string
      name: string | null
      orientation: 'landscape' | 'portrait'
      pageSize: { width: number; height: number }
      dpi: { horizontal: number; vertical: number }
    }>
  >([])

  // Advanced settings visibility
  const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(false)
  const [showSecondaryAdvancedSettings, setShowSecondaryAdvancedSettings] = useState<boolean>(false)
  const [showTertiaryAdvancedSettings, setShowTertiaryAdvancedSettings] = useState<boolean>(false)
  // Position shift visibility
  const [showPositionShift, setShowPositionShift] = useState<boolean>(false)
  const [showSecondaryPositionShift, setShowSecondaryPositionShift] = useState<boolean>(false)
  const [showTertiaryPositionShift, setShowTertiaryPositionShift] = useState<boolean>(false)

  // C# printing toggle
  const {
    useCSharpPrinting,
    setUseCSharpPrinting,
    setCustomSettingsMemory,
    getCustomSettingsMemory,
    useSumatraPDF,
    secondaryUseSumatraPDF,
    tertiaryUseSumatraPDF,
    // Print method + hot-folder config (subscribe to raw fields so the UI re-renders)
    printMethod,
    secondaryPrintMethod,
    tertiaryPrintMethod,
    hotFolderConfig,
    secondaryHotFolderConfig,
    tertiaryHotFolderConfig,
    setPrintMethod,
    setHotFolderConfig
  } = usePrintStore()

  // Set initial preview image from frame
  useEffect(() => {
    if (selectedFrameId) {
      const frame = getFrame(selectedFrameId)
      if (frame?.thumbnail) {
        setPreviewImage(frame.thumbnail)
      }
    } else if (frames[0]?._id) {
      const frame = getFrame(frames[0]._id)
      if (frame?.thumbnail) {
        setPreviewImage(frame.thumbnail)
        setSelectedFrameId(frames[0]._id)
      }
    }
  }, [frames, getFrame, selectedFrameId])

  // Reset temp settings when modal opens (to ensure fresh state from store)
  // Use a ref to track if this is the first render after opening
  const prevIsOpenRef = useRef<boolean>(false)
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      setTempSettings(settings)
      setTempSecondarySettings({
        ...settings,
        ...(secondaryPrinter?.settings || {}),
        orientation: secondaryPrinter?.settings?.orientation || 'landscape',
        pageSize: {
          height: secondaryPrinter?.settings?.pageSize?.height || 105000,
          width: secondaryPrinter?.settings?.pageSize?.width || 156100
        },
        scaleFactor: secondaryPrinter?.settings?.scaleFactor || 100
      })
      setTempTertiarySettings({
        ...settings,
        ...(tertiaryPrinter?.settings || {}),
        orientation: tertiaryPrinter?.settings?.orientation || 'landscape',
        pageSize: {
          height: tertiaryPrinter?.settings?.pageSize?.height || 105000,
          width: tertiaryPrinter?.settings?.pageSize?.width || 156100
        },
        scaleFactor: tertiaryPrinter?.settings?.scaleFactor || 100
      })
      // Reset other temp states
      setMarginType(settings.margins?.marginType || 'default')
      setSecondaryMarginType(secondaryPrinter?.settings?.margins?.marginType || 'default')
      setTertiaryMarginType(tertiaryPrinter?.settings?.margins?.marginType || 'default')
      setMargins({
        top: settings.margins?.top || 0,
        bottom: settings.margins?.bottom || 0,
        left: settings.margins?.left || 0,
        right: settings.margins?.right || 0
      })
      setSecondaryMargins({
        top: secondaryPrinter?.settings?.margins?.top || 0,
        bottom: secondaryPrinter?.settings?.margins?.bottom || 0,
        left: secondaryPrinter?.settings?.margins?.left || 0,
        right: secondaryPrinter?.settings?.margins?.right || 0
      })
      setTertiaryMargins({
        top: tertiaryPrinter?.settings?.margins?.top || 0,
        bottom: tertiaryPrinter?.settings?.margins?.bottom || 0,
        left: tertiaryPrinter?.settings?.margins?.left || 0,
        right: tertiaryPrinter?.settings?.margins?.right || 0
      })
      setDpi({
        horizontal: settings.dpi?.horizontal || 600,
        vertical: settings.dpi?.vertical || 600
      })
      setSecondaryDpi({
        horizontal: secondaryPrinter?.settings?.dpi?.horizontal || 600,
        vertical: secondaryPrinter?.settings?.dpi?.vertical || 600
      })
      setTertiaryDpi({
        horizontal: tertiaryPrinter?.settings?.dpi?.horizontal || 600,
        vertical: tertiaryPrinter?.settings?.dpi?.vertical || 600
      })
      setScaleFactor(settings.scaleFactor || 100)
      setSecondaryScaleFactor(secondaryPrinter?.settings?.scaleFactor || 100)
      setTertiaryScaleFactor(tertiaryPrinter?.settings?.scaleFactor || 100)
      setPrintBackground(settings.printBackground ?? true)
      setSecondaryPrintBackground(secondaryPrinter?.settings?.printBackground ?? true)
      setTertiaryPrintBackground(tertiaryPrinter?.settings?.printBackground ?? true)
      setPrintResolutionScale(settings.printResolutionScale || 1)
      setSecondaryPrintResolutionScale(secondaryPrinter?.settings?.printResolutionScale || 1)
      setTertiaryPrintResolutionScale(tertiaryPrinter?.settings?.printResolutionScale || 1)
      setPositionShift({
        top: settings.printPositionShift?.top || 0,
        bottom: settings.printPositionShift?.bottom ?? -0.5,
        left: settings.printPositionShift?.left || 0,
        right: settings.printPositionShift?.right || 0
      })
      setSecondaryPositionShift({
        top: secondaryPrinter?.settings?.printPositionShift?.top || 0,
        bottom: secondaryPrinter?.settings?.printPositionShift?.bottom ?? -0.5,
        left: secondaryPrinter?.settings?.printPositionShift?.left || 0,
        right: secondaryPrinter?.settings?.printPositionShift?.right || 0
      })
      setTertiaryPositionShift({
        top: tertiaryPrinter?.settings?.printPositionShift?.top || 0,
        bottom: tertiaryPrinter?.settings?.printPositionShift?.bottom ?? -0.5,
        left: tertiaryPrinter?.settings?.printPositionShift?.left || 0,
        right: tertiaryPrinter?.settings?.printPositionShift?.right || 0
      })
      setSelectedPrinter(printStoreSelectedPrinter || '')
      setSelectedSecondaryPrinter(secondaryPrinter?.printerName || '')
      setSelectedTertiaryPrinter(tertiaryPrinter?.printerName || '')
      setSecondaryPurpose(secondaryPrinter?.purpose || 'special-frames')
      setTertiaryPurpose(tertiaryPrinter?.purpose || 'special-frames')
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, settings, secondaryPrinter, tertiaryPrinter, printStoreSelectedPrinter])

  // Sync selected printer with store when it changes
  useEffect(() => {
    setSelectedPrinter(printStoreSelectedPrinter || '')
  }, [printStoreSelectedPrinter])

  // Clear/reset margins and DPI when SumatraPDF mode is enabled
  useEffect(() => {
    if (useSumatraPDF) {
      // Store previous values before resetting
      prevMarginTypeRef.current = marginType
      prevDpiRef.current = dpi
      // Reset to defaults
      setMarginType('default')
      setDpi({ horizontal: 600, vertical: 600 })
    } else {
      // Restore previous values if they exist
      if (prevMarginTypeRef.current) {
        setMarginType(prevMarginTypeRef.current)
      }
      if (prevDpiRef.current) {
        setDpi(prevDpiRef.current)
      }
    }
  }, [useSumatraPDF])

  // Clear/reset margins and DPI for secondary printer when SumatraPDF mode is enabled
  useEffect(() => {
    if (secondaryUseSumatraPDF) {
      // Store previous values before resetting
      prevSecondaryMarginTypeRef.current = secondaryMarginType
      prevSecondaryDpiRef.current = secondaryDpi
      // Reset to defaults
      setSecondaryMarginType('default')
      setSecondaryDpi({ horizontal: 600, vertical: 600 })
    } else {
      // Restore previous values if they exist
      if (prevSecondaryMarginTypeRef.current) {
        setSecondaryMarginType(prevSecondaryMarginTypeRef.current)
      }
      if (prevSecondaryDpiRef.current) {
        setSecondaryDpi(prevSecondaryDpiRef.current)
      }
    }
  }, [secondaryUseSumatraPDF])

  // Clear/reset margins and DPI for tertiary printer when SumatraPDF mode is enabled
  useEffect(() => {
    if (tertiaryUseSumatraPDF) {
      // Store previous values before resetting
      prevTertiaryMarginTypeRef.current = tertiaryMarginType
      prevTertiaryDpiRef.current = tertiaryDpi
      // Reset to defaults
      setTertiaryMarginType('default')
      setTertiaryDpi({ horizontal: 600, vertical: 600 })
    } else {
      // Restore previous values if they exist
      if (prevTertiaryMarginTypeRef.current) {
        setTertiaryMarginType(prevTertiaryMarginTypeRef.current)
      }
      if (prevTertiaryDpiRef.current) {
        setTertiaryDpi(prevTertiaryDpiRef.current)
      }
    }
  }, [tertiaryUseSumatraPDF])

  // Check printer capabilities support on mount
  useEffect(() => {
    // Check if the getPrinterCapabilities API is available
    const api = getPreloadApi()
    const hasSupport = isElectron && typeof api?.getPrinterCapabilities === 'function'
    setHasPrinterCapsSupport(hasSupport)

    if (!hasSupport && isElectron) {
      console.warn(
        'Printer capabilities API not available. Using legacy printer settings mode. Please update your Electron app for smart printer presets.'
      )
    }

    // Fetch app version for SumatraPDF feature availability check
    if (isElectron) {
      void getHostAppVersion()
        .then((version) => setAppVersion(version ?? null))
        .catch((error) => {
          console.error('Failed to get app version:', error)
          setAppVersion(null)
        })
    }
  }, [isElectron])

  // Fetch tunnel status and load capabilities when modal opens
  useEffect(() => {
    if (isOpen && isElectron) {
      const fetchTunnelStatus = async (): Promise<void> => {
        try {
          const status = await getTunnelStatus()
          setTunnelUrl(status.url)
          setIsTunnelActive(status.isActive)
        } catch (error) {
          console.error('Failed to fetch tunnel status:', error)
          setTunnelUrl(null)
          setIsTunnelActive(false)
        }
      }
      fetchTunnelStatus()

      // Load capabilities for currently selected printers (only if supported)
      if (hasPrinterCapsSupport) {
        if (selectedPrinter) {
          loadPrinterCapabilities(selectedPrinter, 'default')
        }
        if (selectedSecondaryPrinter) {
          loadPrinterCapabilities(selectedSecondaryPrinter, 'secondary')
        }
        if (selectedTertiaryPrinter) {
          loadPrinterCapabilities(selectedTertiaryPrinter, 'tertiary')
        }
      }
    }
  }, [
    isOpen,
    isElectron,
    selectedPrinter,
    selectedSecondaryPrinter,
    selectedTertiaryPrinter,
    hasPrinterCapsSupport
  ])

  // Generate combined options from capabilities (default printer)
  useEffect(() => {
    if (!printerCapabilities) {
      setCombinedOptions([])
      setShowAdvancedSettings(true)
      return
    }

    const options: Array<{
      id: string
      name: string | null
      orientation: 'landscape' | 'portrait'
      pageSize: { width: number; height: number }
      dpi: { horizontal: number; vertical: number }
    }> = []

    const pageSizes = printerCapabilities.capabilities.pageSizes || []
    const dpis = printerCapabilities.capabilities.dpis || []

    // If we have both pageSizes and dpis, generate all combinations
    if (pageSizes.length > 0 && dpis.length > 0) {
      pageSizes.forEach((size, sizeIdx) => {
        dpis.forEach((dpi) => {
          (['landscape', 'portrait'] as const).forEach((orientation) => {
            options.push({
              id: `preset-${sizeIdx}-${dpi.x}-${dpi.y}-${orientation}`,
              name: size.name ?? null,
              orientation,
              pageSize: { width: size.widthMicrons, height: size.heightMicrons },
              dpi: { horizontal: dpi.x, vertical: dpi.y }
            })
          })
        })
      })
      setShowAdvancedSettings(false)
    }
    // If we have pageSizes but no dpis, use default dpi
    else if (pageSizes.length > 0) {
      const defaultDpi = printerCapabilities.defaults.dpi || { x: 300, y: 300 }
      pageSizes.forEach((size, sizeIdx) => {
        (['landscape', 'portrait'] as const).forEach((orientation) => {
          options.push({
            id: `preset-${sizeIdx}-${defaultDpi.x}-${defaultDpi.y}-${orientation}`,
            name: size.name ?? null,
            orientation,
            pageSize: { width: size.widthMicrons, height: size.heightMicrons },
            dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
          })
        })
      })
      setShowAdvancedSettings(false)
    }
    // If no capabilities but have defaults, create one option from defaults
    else if (printerCapabilities.defaults.pageSize || printerCapabilities.defaults.dpi) {
      const defaultPageSize = printerCapabilities.defaults.pageSize || {
        name: null,
        widthMicrons: 156100,
        heightMicrons: 105000
      }
      const defaultDpi = printerCapabilities.defaults.dpi || { x: 300, y: 300 }

      options.push({
        id: 'preset-default-landscape',
        name: defaultPageSize.name ?? '#1',
        orientation: 'landscape',
        pageSize: {
          width: defaultPageSize.widthMicrons,
          height: defaultPageSize.heightMicrons
        },
        dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
      })

      options.push({
        id: 'preset-default-portrait',
        name: defaultPageSize.name ?? '#1',
        orientation: 'portrait',
        pageSize: {
          width: defaultPageSize.widthMicrons,
          height: defaultPageSize.heightMicrons
        },
        dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
      })

      setShowAdvancedSettings(true) // Show advanced since we only have defaults
    } else {
      setShowAdvancedSettings(true)
    }

    setCombinedOptions(options)
  }, [printerCapabilities])

  // Generate combined options from capabilities (secondary printer)
  useEffect(() => {
    if (!secondaryPrinterCapabilities) {
      setSecondaryCombinedOptions([])
      setShowSecondaryAdvancedSettings(true)
      return
    }

    const options: Array<{
      id: string
      name: string | null
      orientation: 'landscape' | 'portrait'
      pageSize: { width: number; height: number }
      dpi: { horizontal: number; vertical: number }
    }> = []

    const pageSizes = secondaryPrinterCapabilities.capabilities.pageSizes || []
    const dpis = secondaryPrinterCapabilities.capabilities.dpis || []

    if (pageSizes.length > 0 && dpis.length > 0) {
      pageSizes.forEach((size, sizeIdx) => {
        dpis.forEach((dpi) => {
          (['landscape', 'portrait'] as const).forEach((orientation) => {
            options.push({
              id: `preset-${sizeIdx}-${dpi.x}-${dpi.y}-${orientation}`,
              name: size.name ?? null,
              orientation,
              pageSize: { width: size.widthMicrons, height: size.heightMicrons },
              dpi: { horizontal: dpi.x, vertical: dpi.y }
            })
          })
        })
      })
      setShowSecondaryAdvancedSettings(false)
    } else if (pageSizes.length > 0) {
      const defaultDpi = secondaryPrinterCapabilities.defaults.dpi || { x: 300, y: 300 }
      pageSizes.forEach((size, sizeIdx) => {
        (['landscape', 'portrait'] as const).forEach((orientation) => {
          options.push({
            id: `preset-${sizeIdx}-${defaultDpi.x}-${defaultDpi.y}-${orientation}`,
            name: size.name ?? null,
            orientation,
            pageSize: { width: size.widthMicrons, height: size.heightMicrons },
            dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
          })
        })
      })
      setShowSecondaryAdvancedSettings(false)
    } else if (
      secondaryPrinterCapabilities.defaults.pageSize ||
      secondaryPrinterCapabilities.defaults.dpi
    ) {
      const defaultPageSize = secondaryPrinterCapabilities.defaults.pageSize || {
        name: null,
        widthMicrons: 156100,
        heightMicrons: 105000
      }
      const defaultDpi = secondaryPrinterCapabilities.defaults.dpi || { x: 300, y: 300 }

      options.push({
        id: 'preset-default-landscape',
        name: defaultPageSize.name ?? '#1',
        orientation: 'landscape',
        pageSize: {
          width: defaultPageSize.widthMicrons,
          height: defaultPageSize.heightMicrons
        },
        dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
      })

      options.push({
        id: 'preset-default-portrait',
        name: defaultPageSize.name ?? '#1',
        orientation: 'portrait',
        pageSize: {
          width: defaultPageSize.widthMicrons,
          height: defaultPageSize.heightMicrons
        },
        dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
      })

      setShowSecondaryAdvancedSettings(true)
    } else {
      setShowSecondaryAdvancedSettings(true)
    }

    setSecondaryCombinedOptions(options)
  }, [secondaryPrinterCapabilities])

  // Generate combined options from capabilities (tertiary printer)
  useEffect(() => {
    if (!tertiaryPrinterCapabilities) {
      setTertiaryCombinedOptions([])
      setShowTertiaryAdvancedSettings(true)
      return
    }

    const options: Array<{
      id: string
      name: string | null
      orientation: 'landscape' | 'portrait'
      pageSize: { width: number; height: number }
      dpi: { horizontal: number; vertical: number }
    }> = []

    const pageSizes = tertiaryPrinterCapabilities.capabilities.pageSizes || []
    const dpis = tertiaryPrinterCapabilities.capabilities.dpis || []

    if (pageSizes.length > 0 && dpis.length > 0) {
      pageSizes.forEach((size, sizeIdx) => {
        dpis.forEach((dpi) => {
          (['landscape', 'portrait'] as const).forEach((orientation) => {
            options.push({
              id: `preset-${sizeIdx}-${dpi.x}-${dpi.y}-${orientation}`,
              name: size.name ?? null,
              orientation,
              pageSize: { width: size.widthMicrons, height: size.heightMicrons },
              dpi: { horizontal: dpi.x, vertical: dpi.y }
            })
          })
        })
      })
      setShowTertiaryAdvancedSettings(false)
    } else if (pageSizes.length > 0) {
      const defaultDpi = tertiaryPrinterCapabilities.defaults.dpi || { x: 300, y: 300 }
      pageSizes.forEach((size, sizeIdx) => {
        (['landscape', 'portrait'] as const).forEach((orientation) => {
          options.push({
            id: `preset-${sizeIdx}-${defaultDpi.x}-${defaultDpi.y}-${orientation}`,
            name: size.name ?? null,
            orientation,
            pageSize: { width: size.widthMicrons, height: size.heightMicrons },
            dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
          })
        })
      })
      setShowTertiaryAdvancedSettings(false)
    } else if (
      tertiaryPrinterCapabilities.defaults.pageSize ||
      tertiaryPrinterCapabilities.defaults.dpi
    ) {
      const defaultPageSize = tertiaryPrinterCapabilities.defaults.pageSize || {
        name: null,
        widthMicrons: 156100,
        heightMicrons: 105000
      }
      const defaultDpi = tertiaryPrinterCapabilities.defaults.dpi || { x: 300, y: 300 }

      options.push({
        id: 'preset-default-landscape',
        name: defaultPageSize.name ?? '#1',
        orientation: 'landscape',
        pageSize: {
          width: defaultPageSize.widthMicrons,
          height: defaultPageSize.heightMicrons
        },
        dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
      })

      options.push({
        id: 'preset-default-portrait',
        name: defaultPageSize.name ?? '#1',
        orientation: 'portrait',
        pageSize: {
          width: defaultPageSize.widthMicrons,
          height: defaultPageSize.heightMicrons
        },
        dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
      })

      setShowTertiaryAdvancedSettings(true)
    } else {
      setShowTertiaryAdvancedSettings(true)
    }

    setTertiaryCombinedOptions(options)
  }, [tertiaryPrinterCapabilities])

  // Load printer capabilities
  const loadPrinterCapabilities = async (
    printerName: string,
    printerType: 'default' | 'secondary' | 'tertiary' = 'default'
  ): Promise<void> => {
    if (!printerName || !isElectron) return

    setLoadingCapabilities(true)
    try {
      const capabilities = await PrintUtils.getPrinterCapabilities(printerName)
      if (printerType === 'secondary') {
        setSecondaryPrinterCapabilities(capabilities)
      } else if (printerType === 'tertiary') {
        setTertiaryPrinterCapabilities(capabilities)
      } else {
        setPrinterCapabilities(capabilities)
      }
    } catch (error) {
      console.error('Failed to load printer capabilities:', error)
    } finally {
      setLoadingCapabilities(false)
    }
  }

  // Handle preset selection from combined dropdown
  const handlePresetChange = (
    presetId: string,
    printerType: 'default' | 'secondary' | 'tertiary' = 'default'
  ): void => {
    const currentSettings =
      printerType === 'secondary'
        ? tempSecondarySettings
        : printerType === 'tertiary'
          ? tempTertiarySettings
          : tempSettings
    const currentDpi =
      printerType === 'secondary' ? secondaryDpi : printerType === 'tertiary' ? tertiaryDpi : dpi
    const currentPresetId =
      printerType === 'secondary'
        ? selectedSecondaryPresetId
        : printerType === 'tertiary'
          ? selectedTertiaryPresetId
          : selectedPresetId

    // If switching FROM custom to a preset, save current custom settings
    if (currentPresetId === 'custom' && presetId !== 'custom') {
      setCustomSettingsMemory(
        {
          orientation: currentSettings.orientation,
          pageSize: currentSettings.pageSize,
          dpi: currentDpi
        },
        printerType
      )
    }

    if (printerType === 'secondary') {
      setSelectedSecondaryPresetId(presetId)
    } else if (printerType === 'tertiary') {
      setSelectedTertiaryPresetId(presetId)
    } else {
      setSelectedPresetId(presetId)
    }

    if (presetId === 'custom') {
      // Restore saved custom settings if available
      const savedCustom = getCustomSettingsMemory(printerType)
      if (savedCustom) {
        if (printerType === 'secondary') {
          setTempSecondarySettings((prev) => ({
            ...prev,
            orientation: savedCustom.orientation,
            pageSize: savedCustom.pageSize
          }))
          setSecondaryDpi(savedCustom.dpi)
        } else if (printerType === 'tertiary') {
          setTempTertiarySettings((prev) => ({
            ...prev,
            orientation: savedCustom.orientation,
            pageSize: savedCustom.pageSize
          }))
          setTertiaryDpi(savedCustom.dpi)
        } else {
          setTempSettings((prev) => ({
            ...prev,
            orientation: savedCustom.orientation,
            pageSize: savedCustom.pageSize
          }))
          setDpi(savedCustom.dpi)
        }
      }
      return
    }

    const options =
      printerType === 'secondary'
        ? secondaryCombinedOptions
        : printerType === 'tertiary'
          ? tertiaryCombinedOptions
          : combinedOptions
    const preset = options.find((opt) => opt.id === presetId)
    if (!preset) return

    // Update all settings based on preset
    if (printerType === 'secondary') {
      setTempSecondarySettings((prev) => ({
        ...prev,
        orientation: preset.orientation,
        pageSize: preset.pageSize
      }))
      setSecondaryDpi(preset.dpi)
    } else if (printerType === 'tertiary') {
      setTempTertiarySettings((prev) => ({
        ...prev,
        orientation: preset.orientation,
        pageSize: preset.pageSize
      }))
      setTertiaryDpi(preset.dpi)
    } else {
      setTempSettings((prev) => ({
        ...prev,
        orientation: preset.orientation,
        pageSize: preset.pageSize
      }))
      setDpi(preset.dpi)
    }
  }

  const convertToBase64 = async (url: string): Promise<string> => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = (): void => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.error('Error converting image to base64:', error)
      return url // Fallback to original URL if conversion fails
    }
  }

  const handleFileUpload = useCallback((file: File): void => {
    const reader = new FileReader()
    reader.onload = (event): void => {
      const imageUrl = event.target?.result as string
      setPreviewImage(imageUrl)
    }
    reader.readAsDataURL(file)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault()
      setIsDragging(false)
      const file = e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        handleFileUpload(file)
      }
    },
    [handleFileUpload]
  )

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  // Save all tabs' settings to store
  const saveAllTabs = (): void => {
    // Save default printer settings
    const newSettings = {
      ...tempSettings,
      orientation: tempSettings.orientation,
      pageSize: tempSettings.pageSize,
      margins: {
        marginType,
        ...(marginType === 'custom' ? margins : {})
      },
      dpi,
      scaleFactor,
      printBackground,
      printResolutionScale,
      printPositionShift: positionShift
    }
    updateSettings(newSettings)

    // Save secondary printer settings
    setSecondaryPrinter({
      printerName: selectedSecondaryPrinter || '',
      settings: {
        ...tempSecondarySettings,
        orientation: tempSecondarySettings.orientation,
        pageSize: tempSecondarySettings.pageSize,
        margins: {
          marginType: secondaryMarginType,
          ...(secondaryMarginType === 'custom' ? secondaryMargins : {})
        },
        dpi: secondaryDpi,
        scaleFactor: secondaryScaleFactor,
        printBackground: secondaryPrintBackground,
        printResolutionScale: secondaryPrintResolutionScale,
        printPositionShift: secondaryPositionShift
      },
      frameIds: secondaryPrinter?.frameIds ? [...secondaryPrinter.frameIds] : [],
      purpose: secondaryPurpose
    })

    // Save tertiary printer settings
    setTertiaryPrinter({
      printerName: selectedTertiaryPrinter || '',
      settings: {
        ...tempTertiarySettings,
        orientation: tempTertiarySettings.orientation,
        pageSize: tempTertiarySettings.pageSize,
        margins: {
          marginType: tertiaryMarginType,
          ...(tertiaryMarginType === 'custom' ? tertiaryMargins : {})
        },
        dpi: tertiaryDpi,
        scaleFactor: tertiaryScaleFactor,
        printBackground: tertiaryPrintBackground,
        printResolutionScale: tertiaryPrintResolutionScale,
        printPositionShift: tertiaryPositionShift
      },
      frameIds: tertiaryPrinter?.frameIds ? [...tertiaryPrinter.frameIds] : [],
      purpose: tertiaryPurpose
    })
  }

  const handlePreview = async (): Promise<void> => {
    if (!previewImage) return

    // Convert image to base64 if it's not already
    const base64Image = previewImage.startsWith('data:')
      ? previewImage
      : await convertToBase64(previewImage)

    try {
      const currentSettings =
        activeTab === 'default'
          ? tempSettings
          : activeTab === 'secondary'
            ? tempSecondarySettings
            : tempTertiarySettings
      const currentMargins =
        activeTab === 'default'
          ? margins
          : activeTab === 'secondary'
            ? secondaryMargins
            : tertiaryMargins
      const currentMarginType =
        activeTab === 'default'
          ? marginType
          : activeTab === 'secondary'
            ? secondaryMarginType
            : tertiaryMarginType
      const currentDpi =
        activeTab === 'default' ? dpi : activeTab === 'secondary' ? secondaryDpi : tertiaryDpi
      const currentScaleFactor =
        activeTab === 'default'
          ? scaleFactor
          : activeTab === 'secondary'
            ? secondaryScaleFactor
            : tertiaryScaleFactor
      const currentPrintBackground =
        activeTab === 'default'
          ? printBackground
          : activeTab === 'secondary'
            ? secondaryPrintBackground
            : tertiaryPrintBackground
      const currentPrintResolutionScale =
        activeTab === 'default'
          ? printResolutionScale
          : activeTab === 'secondary'
            ? secondaryPrintResolutionScale
            : tertiaryPrintResolutionScale
      const currentPositionShift =
        activeTab === 'default'
          ? positionShift
          : activeTab === 'secondary'
            ? secondaryPositionShift
            : tertiaryPositionShift
      const currentPrinter =
        activeTab === 'default'
          ? selectedPrinter
          : activeTab === 'secondary'
            ? selectedSecondaryPrinter
            : selectedTertiaryPrinter

      // Validate printer selection
      if (!currentPrinter && hasElectronPreload()) {
        cogoToast.error(
          t('printerSettings.feedback.selectPrinter')
        )
        return
      }

      // Log settings for debugging
      console.log('Print settings:', {
        printer: currentPrinter,
        settings: {
          orientation: currentSettings.orientation,
          pageSize: currentSettings.pageSize,
          margins: {
            marginType: currentMarginType,
            ...(currentMarginType === 'custom' ? currentMargins : {})
          },
          dpi: currentDpi,
          scaleFactor: currentScaleFactor,
          printBackground: currentPrintBackground,
          printResolutionScale: currentPrintResolutionScale
        }
      })

      const result = await PrintUtils.sendToPrinter(base64Image, {
        orientation: currentSettings.orientation,
        pageSize: currentSettings.pageSize,
        printerName: currentPrinter || 'DS-RX1',
        copies: isNativeShell ? copies : 1,
        margins: {
          marginType: currentMarginType,
          ...(currentMarginType === 'custom' ? currentMargins : {})
        },
        dpi: currentDpi,
        scaleFactor: currentScaleFactor,
        printBackground: currentPrintBackground,
        silent: true,
        printResolutionScale: currentPrintResolutionScale,
        printPositionShift: currentPositionShift
      })

      if (result.success) {
        cogoToast.success(t('printerSettings.feedback.printInitiated'))
        logPrintEvent({ eventType: 'test_print', copies: isNativeShell ? copies : 1, printerName: currentPrinter || 'DS-RX1', success: true })
      } else {
        console.error('Print failed:', result.error)
        cogoToast.error(
          `${t('printerSettings.feedback.printFailed')}: ${result.failureReason || result.error || 'Unknown error'}`
        )
      }
    } catch (error) {
      console.error('Print error:', error)
      cogoToast.error(t('printerSettings.feedback.printFailed'))
    }
  }

  if (!isOpen) return null

  const renderPrinterSettings = (
    printerType: 'default' | 'secondary' | 'tertiary' = 'default'
  ): JSX.Element => {
    const isSecondary = printerType === 'secondary'
    const isTertiary = printerType === 'tertiary'
    const currentSettings = isSecondary
      ? tempSecondarySettings
      : isTertiary
        ? tempTertiarySettings
        : tempSettings
    const currentMargins = isSecondary ? secondaryMargins : isTertiary ? tertiaryMargins : margins
    const currentMarginType = isSecondary
      ? secondaryMarginType
      : isTertiary
        ? tertiaryMarginType
        : marginType
    const currentPositionShift = isSecondary
      ? secondaryPositionShift
      : isTertiary
        ? tertiaryPositionShift
        : positionShift
    const currentDpi = isSecondary ? secondaryDpi : isTertiary ? tertiaryDpi : dpi
    const currentScaleFactor = isSecondary
      ? secondaryScaleFactor
      : isTertiary
        ? tertiaryScaleFactor
        : scaleFactor
    const currentPrintBackground = isSecondary
      ? secondaryPrintBackground
      : isTertiary
        ? tertiaryPrintBackground
        : printBackground
    const currentPrintResolutionScale = isSecondary
      ? secondaryPrintResolutionScale
      : isTertiary
        ? tertiaryPrintResolutionScale
        : printResolutionScale
    const currentPrinter = isSecondary
      ? selectedSecondaryPrinter
      : isTertiary
        ? selectedTertiaryPrinter
        : selectedPrinter
    const setCurrentPrinter = isSecondary
      ? setSelectedSecondaryPrinter
      : isTertiary
        ? setSelectedTertiaryPrinter
        : setSelectedPrinter
    const setcurrentPrintResolutionScale = isSecondary
      ? setSecondaryPrintResolutionScale
      : isTertiary
        ? setTertiaryPrintResolutionScale
        : setPrintResolutionScale
    const currentOptions = isSecondary
      ? secondaryCombinedOptions
      : isTertiary
        ? tertiaryCombinedOptions
        : combinedOptions
    const currentPresetId = isSecondary
      ? selectedSecondaryPresetId
      : isTertiary
        ? selectedTertiaryPresetId
        : selectedPresetId
    const currentShowAdvanced = isSecondary
      ? showSecondaryAdvancedSettings
      : isTertiary
        ? showTertiaryAdvancedSettings
        : showAdvancedSettings
    const setCurrentShowAdvanced = isSecondary
      ? setShowSecondaryAdvancedSettings
      : isTertiary
        ? setShowTertiaryAdvancedSettings
        : setShowAdvancedSettings

    // SumatraPDF settings
    const currentUseSumatraPDF = isSecondary
      ? secondaryUseSumatraPDF
      : isTertiary
        ? tertiaryUseSumatraPDF
        : useSumatraPDF
    // Print method + hot-folder config for the active printer slot.
    const methodSlot: 'default' | 'secondary' | 'tertiary' = isSecondary
      ? 'secondary'
      : isTertiary
        ? 'tertiary'
        : 'default'
    const rawPrintMethod = isSecondary
      ? secondaryPrintMethod
      : isTertiary
        ? tertiaryPrintMethod
        : printMethod
    // Derive method, falling back to the legacy SumatraPDF boolean when unset.
    const currentPrintMethod: PrintMethod =
      rawPrintMethod ?? (currentUseSumatraPDF ? 'sumatra' : 'electron')
    const currentHotFolderConfig = isSecondary
      ? secondaryHotFolderConfig
      : isTertiary
        ? tertiaryHotFolderConfig
        : hotFolderConfig
    const isHotFolder = currentPrintMethod === 'hotFolder'

    // Contextual help shown under the print-method selector for the chosen method.
    const printMethodHelp: Record<PrintMethod, string> = {
      electron: t(
        'printerSettings.printMethod.help.appPrint',
        'Recommended. The app controls the size, layout and quality, then sends the print. Best for standard sizes (4×6, strips, 5×7).'
      ),
      sumatra: t(
        'printerSettings.printMethod.help.driver',
        "Sends to the printer using the driver's own default settings for margins and DPI. Better compatibility with some custom printer drivers."
      ),
      hotFolder: t(
        'printerSettings.printMethod.help.hotFolder',
        'Drops each print into a watched folder monitored by external print software (e.g. DNP Hot Folder Print). Use this for output sizes the normal driver can’t produce, such as panoramic (6×14 / 6×20).'
      )
    }

    // Check if version supports SumatraPDF (>= 2.0.2)
    const supportsSumatraPDF = ((): boolean => {
      if (!isElectron || !appVersion) return false

      const versionParts = appVersion.split('.').map(Number)
      const minVersionParts = [2, 0, 2].map(Number)

      for (let i = 0; i < Math.max(versionParts.length, minVersionParts.length); i++) {
        const versionPart = versionParts[i] || 0
        const minPart = minVersionParts[i] || 0

        if (versionPart > minPart) return true
        if (versionPart < minPart) return false
      }

      return true // Versions are equal
    })()

    // Hot Folder support is feature-detected from the bundled main process, not
    // version-gated: the renderer is web-served and reaches every booth instantly,
    // but the file-writing only exists in updated .exe builds. Still show the
    // option if it's already the saved method (so the value isn't silently lost).
    const supportsHotFolder =
      (isElectron && getPreloadApi()?.hotFolderSupported === true) || isHotFolder

    return (
      <div className="space-y-6">
        {/* a) Printer Dropdown */}
        {isNativeShell && (
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              {t('settings.printer.title')}
            </label>
            <div className="flex gap-2">
              <select
                value={currentPrinter || 'system-default'}
                onChange={(e) => {
                  const selectedValue = e.target.value === 'system-default' ? '' : e.target.value
                  setCurrentPrinter(selectedValue)
                  if (printerType === 'default') {
                    updateGlobalSelectedPrinter(selectedValue)
                  }
                  if (selectedValue) {
                    loadPrinterCapabilities(selectedValue, printerType)
                  }
                }}
                className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                aria-label={t('settings.printer.selectPrinter')}
              >
                <option value="system-default">
                  {t('settings.printer.systemDefault')}
                </option>
                {devices?.printers.map((printer) => (
                  <option key={printer} value={printer}>
                    {printer}
                  </option>
                ))}
              </select>
              {(isSecondary || isTertiary) &&
                (printerType === 'secondary'
                  ? secondaryPurpose === 'special-frames'
                  : tertiaryPurpose === 'special-frames') && (
                  <Button
                    text={t('printerSettings.selectFrames')}
                    variant="secondary"
                    onClick={() => {
                      setFrameSelectionPrinterType(
                        printerType === 'secondary' ? 'secondary' : 'tertiary'
                      )
                      setIsFrameSelectionOpen(true)
                    }}
                    className="!px-2 !py-0 !text-lg"
                  />
                )}
            </div>

            {loadingCapabilities && (
              <div className="mt-2 text-xs text-blue-600">
                {t('printerSettings.smartSetup.loading')}
              </div>
            )}
          </div>
        )}

        {!isNativeShell && (
          <div className="mt-4 space-y-2">
            <p className="text-sm text-gray-600">
              {t('settings.printer.notAvailable')}
            </p>
            <a
              href={`${process.env.VITE_API_URL}/dashboard/installation`}
              className="inline-flex items-center text-sm text-blue-500 hover:text-blue-600"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaDownload className="mr-1" />
              {t(
                'settings.printer.downloadDesktop',
                'Download desktop version for full printer support'
              )}
            </a>
          </div>
        )}

        {isNativeShell && (
          <>
            {/* Smart printer presets (Electron only; Tauri shows legacy manual config below) */}
            {/* Hide presets when System Default (empty printer) + SumatraPDF enabled */}
            {hasPrinterCapsSupport &&
            !(currentUseSumatraPDF && !currentPrinter) &&
            !isHotFolder &&
            currentPrinter ? (
              <>
                {/* b) Combined Size/Orientation/DPI Dropdown - Always show when printer selected */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('printerSettings.preset')}
                  </label>
                  <select
                    value={currentPresetId}
                    onChange={(e) => handlePresetChange(e.target.value, printerType)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    title={t('printerSettings.preset')}
                  >
                    {currentOptions.length > 0 ? (
                      currentOptions.map((opt, idx) => {
                        const displayName =
                          opt.name && opt.name !== 'Unknown' ? opt.name : `#${idx + 1}`
                        const widthMm = PrintUtils.micronsToMm(opt.pageSize.width)
                        const heightMm = PrintUtils.micronsToMm(opt.pageSize.height)
                        return (
                          <option key={opt.id} value={opt.id}>
                            {displayName} ({widthMm}×{heightMm}mm) - {opt.orientation} -{' '}
                            {opt.dpi.horizontal}×{opt.dpi.vertical} DPI
                          </option>
                        )
                      })
                    ) : (
                      <option value="custom">
                        {t('printerSettings.noCapabilities')}
                      </option>
                    )}
                    <option value="custom">{t('printerSettings.custom')}</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {currentOptions.length > 0
                      ? t('printerSettings.presetHelp')
                      : t('printerSettings.noCapabilitiesHelp')}
                  </p>
                </div>

                {/* Details Section - Right under preset dropdown */}
                <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                  {currentOptions.length > 0 ? (
                    <button
                      onClick={() => setCurrentShowAdvanced(!currentShowAdvanced)}
                      className={`w-full flex items-center justify-between text-sm font-medium text-gray-700 ${currentShowAdvanced ? 'mb-3' : 'mb-0'}`}
                      type="button"
                    >
                      <span>{t('printerSettings.details')}</span>
                      <span>{currentShowAdvanced ? '▼' : '▶'}</span>
                    </button>
                  ) : (
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      {t('printerSettings.details')}
                    </h3>
                  )}

                  {currentShowAdvanced && (
                    <div className="space-y-4">
                      {/* Manual Orientation */}
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {t('printerSettings.orientation')}
                        </label>
                        <select
                          title={t('printerSettings.orientation')}
                          value={currentSettings.orientation}
                          onChange={(e) => {
                            if (isSecondary) {
                              setTempSecondarySettings((prev) => ({
                                ...prev,
                                orientation: e.target.value as 'landscape' | 'portrait'
                              }))
                              setSelectedSecondaryPresetId('custom')
                            } else if (isTertiary) {
                              setTempTertiarySettings((prev) => ({
                                ...prev,
                                orientation: e.target.value as 'landscape' | 'portrait'
                              }))
                              setSelectedTertiaryPresetId('custom')
                            } else {
                              setTempSettings((prev) => ({
                                ...prev,
                                orientation: e.target.value as 'landscape' | 'portrait'
                              }))
                              setSelectedPresetId('custom')
                            }
                          }}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="landscape">
                            {t('printerSettings.landscape')}
                          </option>
                          <option value="portrait">
                            {t('printerSettings.portrait')}
                          </option>
                        </select>
                      </div>

                      {/* Manual Page Size */}
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {t('printerSettings.pageSize')}
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">
                              {t('printerSettings.width')}
                            </label>
                            <input
                              type="number"
                              value={currentSettings.pageSize.width}
                              onChange={(e) => {
                                if (isSecondary) {
                                  setTempSecondarySettings((prev) => ({
                                    ...prev,
                                    pageSize: {
                                      ...prev.pageSize,
                                      width: parseInt(e.target.value)
                                    }
                                  }))
                                  setSelectedSecondaryPresetId('custom')
                                } else if (isTertiary) {
                                  setTempTertiarySettings((prev) => ({
                                    ...prev,
                                    pageSize: {
                                      ...prev.pageSize,
                                      width: parseInt(e.target.value)
                                    }
                                  }))
                                  setSelectedTertiaryPresetId('custom')
                                } else {
                                  setTempSettings((prev) => ({
                                    ...prev,
                                    pageSize: {
                                      ...prev.pageSize,
                                      width: parseInt(e.target.value)
                                    }
                                  }))
                                  setSelectedPresetId('custom')
                                }
                              }}
                              title={t('printerSettings.width')}
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">
                              {t('printerSettings.height')}
                            </label>
                            <input
                              type="number"
                              value={currentSettings.pageSize.height}
                              onChange={(e) => {
                                if (isSecondary) {
                                  setTempSecondarySettings((prev) => ({
                                    ...prev,
                                    pageSize: {
                                      ...prev.pageSize,
                                      height: parseInt(e.target.value)
                                    }
                                  }))
                                  setSelectedSecondaryPresetId('custom')
                                } else if (isTertiary) {
                                  setTempTertiarySettings((prev) => ({
                                    ...prev,
                                    pageSize: {
                                      ...prev.pageSize,
                                      height: parseInt(e.target.value)
                                    }
                                  }))
                                  setSelectedTertiaryPresetId('custom')
                                } else {
                                  setTempSettings((prev) => ({
                                    ...prev,
                                    pageSize: {
                                      ...prev.pageSize,
                                      height: parseInt(e.target.value)
                                    }
                                  }))
                                  setSelectedPresetId('custom')
                                }
                              }}
                              title={t('printerSettings.height')}
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Manual DPI */}
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {t('printerSettings.dpi')}
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">
                              {t('printerSettings.dpi.horizontal')}
                            </label>
                            <input
                              type="number"
                              value={currentDpi.horizontal}
                              onChange={(e) => {
                                if (isSecondary) {
                                  setSecondaryDpi((prev) => ({
                                    ...prev,
                                    horizontal: parseInt(e.target.value)
                                  }))
                                  setSelectedSecondaryPresetId('custom')
                                } else if (isTertiary) {
                                  setTertiaryDpi((prev) => ({
                                    ...prev,
                                    horizontal: parseInt(e.target.value)
                                  }))
                                  setSelectedTertiaryPresetId('custom')
                                } else {
                                  setDpi((prev) => ({
                                    ...prev,
                                    horizontal: parseInt(e.target.value)
                                  }))
                                  setSelectedPresetId('custom')
                                }
                              }}
                              title={t('printerSettings.dpi.horizontal')}
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">
                              {t('printerSettings.dpi.vertical')}
                            </label>
                            <input
                              type="number"
                              value={currentDpi.vertical}
                              onChange={(e) => {
                                if (isSecondary) {
                                  setSecondaryDpi((prev) => ({
                                    ...prev,
                                    vertical: parseInt(e.target.value)
                                  }))
                                  setSelectedSecondaryPresetId('custom')
                                } else if (isTertiary) {
                                  setTertiaryDpi((prev) => ({
                                    ...prev,
                                    vertical: parseInt(e.target.value)
                                  }))
                                  setSelectedTertiaryPresetId('custom')
                                } else {
                                  setDpi((prev) => ({
                                    ...prev,
                                    vertical: parseInt(e.target.value)
                                  }))
                                  setSelectedPresetId('custom')
                                }
                              }}
                              title={t('printerSettings.dpi.vertical')}
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Legacy manual configuration (for older Electron versions) */}
                {/* Hide when System Default + SumatraPDF enabled, or in hot-folder mode */}
                {!(currentUseSumatraPDF && !currentPrinter) && !isHotFolder && (
                  <>
                    {/* Orientation */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        {t('printerSettings.orientation')}
                      </label>
                      <select
                        title={t('printerSettings.orientation')}
                        value={currentSettings.orientation}
                        onChange={(e) =>
                          isSecondary
                            ? setTempSecondarySettings((prev) => ({
                                ...prev,
                                orientation: e.target.value as 'landscape' | 'portrait'
                              }))
                            : isTertiary
                              ? setTempTertiarySettings((prev) => ({
                                  ...prev,
                                  orientation: e.target.value as 'landscape' | 'portrait'
                                }))
                              : setTempSettings((prev) => ({
                                  ...prev,
                                  orientation: e.target.value as 'landscape' | 'portrait'
                                }))
                        }
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="landscape">
                          {t('printerSettings.landscape')}
                        </option>
                        <option value="portrait">
                          {t('printerSettings.portrait')}
                        </option>
                      </select>
                    </div>

                    {/* Page Size */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        {t('printerSettings.pageSize')}
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            {t('printerSettings.width')}
                          </label>
                          <input
                            type="number"
                            value={currentSettings.pageSize.width}
                            onChange={(e) =>
                              isSecondary
                                ? setTempSecondarySettings((prev) => ({
                                    ...prev,
                                    pageSize: {
                                      ...prev.pageSize,
                                      width: parseInt(e.target.value)
                                    }
                                  }))
                                : isTertiary
                                  ? setTempTertiarySettings((prev) => ({
                                      ...prev,
                                      pageSize: {
                                        ...prev.pageSize,
                                        width: parseInt(e.target.value)
                                      }
                                    }))
                                  : setTempSettings((prev) => ({
                                      ...prev,
                                      pageSize: {
                                        ...prev.pageSize,
                                        width: parseInt(e.target.value)
                                      }
                                    }))
                            }
                            title={t('printerSettings.width')}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            {t('printerSettings.height')}
                          </label>
                          <input
                            type="number"
                            value={currentSettings.pageSize.height}
                            onChange={(e) =>
                              isSecondary
                                ? setTempSecondarySettings((prev) => ({
                                    ...prev,
                                    pageSize: {
                                      ...prev.pageSize,
                                      height: parseInt(e.target.value)
                                    }
                                  }))
                                : isTertiary
                                  ? setTempTertiarySettings((prev) => ({
                                      ...prev,
                                      pageSize: {
                                        ...prev.pageSize,
                                        height: parseInt(e.target.value)
                                      }
                                    }))
                                  : setTempSettings((prev) => ({
                                      ...prev,
                                      pageSize: {
                                        ...prev.pageSize,
                                        height: parseInt(e.target.value)
                                      }
                                    }))
                            }
                            title={t('printerSettings.height')}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Print Quality (DPI) - Hide when using printer driver defaults */}
                    {!currentUseSumatraPDF && !isHotFolder && (
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {t('printerSettings.dpi')}
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">
                              {t('printerSettings.dpi.horizontal')}
                            </label>
                            <input
                              type="number"
                              value={currentDpi.horizontal}
                              onChange={(e) =>
                                isSecondary
                                  ? setSecondaryDpi((prev) => ({
                                      ...prev,
                                      horizontal: parseInt(e.target.value)
                                    }))
                                  : isTertiary
                                    ? setTertiaryDpi((prev) => ({
                                        ...prev,
                                        horizontal: parseInt(e.target.value)
                                      }))
                                    : setDpi((prev) => ({
                                        ...prev,
                                        horizontal: parseInt(e.target.value)
                                      }))
                              }
                              title={t('printerSettings.dpi.horizontal')}
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">
                              {t('printerSettings.dpi.vertical')}
                            </label>
                            <input
                              type="number"
                              value={currentDpi.vertical}
                              onChange={(e) =>
                                isSecondary
                                  ? setSecondaryDpi((prev) => ({
                                      ...prev,
                                      vertical: parseInt(e.target.value)
                                    }))
                                  : isTertiary
                                    ? setTertiaryDpi((prev) => ({
                                        ...prev,
                                        vertical: parseInt(e.target.value)
                                      }))
                                    : setDpi((prev) => ({
                                        ...prev,
                                        vertical: parseInt(e.target.value)
                                      }))
                              }
                              title={t('printerSettings.dpi.vertical')}
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* c) Margins Dropdown - Hide when using printer driver defaults */}
            {!currentUseSumatraPDF && !isHotFolder && (
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t('printerSettings.margins')}
                </label>
                <select
                  value={currentMarginType}
                  onChange={(e) =>
                    isSecondary
                      ? setSecondaryMarginType(
                          e.target.value as 'default' | 'none' | 'printableArea' | 'custom'
                        )
                      : isTertiary
                        ? setTertiaryMarginType(
                            e.target.value as 'default' | 'none' | 'printableArea' | 'custom'
                          )
                        : setMarginType(
                            e.target.value as 'default' | 'none' | 'printableArea' | 'custom'
                          )
                  }
                  title={t('printerSettings.margins')}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-2"
                >
                  <option value="default">{t('printerSettings.margins.default')}</option>
                  <option value="none">{t('printerSettings.margins.none')}</option>
                  <option value="printableArea">
                    {t('printerSettings.margins.printableArea')}
                  </option>
                  <option value="custom">{t('printerSettings.margins.custom')}</option>
                </select>

                {currentMarginType === 'custom' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        {t('printerSettings.margins.top')}
                      </label>
                      <input
                        type="number"
                        value={currentMargins.top}
                        onChange={(e) =>
                          isSecondary
                            ? setSecondaryMargins((prev) => ({
                                ...prev,
                                top: parseInt(e.target.value)
                              }))
                            : isTertiary
                              ? setTertiaryMargins((prev) => ({
                                  ...prev,
                                  top: parseInt(e.target.value)
                                }))
                              : setMargins((prev) => ({ ...prev, top: parseInt(e.target.value) }))
                        }
                        title={t('printerSettings.margins.topTitle')}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        {t('printerSettings.margins.bottom')}
                      </label>
                      <input
                        type="number"
                        value={currentMargins.bottom}
                        onChange={(e) =>
                          isSecondary
                            ? setSecondaryMargins((prev) => ({
                                ...prev,
                                bottom: parseInt(e.target.value)
                              }))
                            : isTertiary
                              ? setTertiaryMargins((prev) => ({
                                  ...prev,
                                  bottom: parseInt(e.target.value)
                                }))
                              : setMargins((prev) => ({
                                  ...prev,
                                  bottom: parseInt(e.target.value)
                                }))
                        }
                        title={t('printerSettings.margins.bottomTitle')}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        {t('printerSettings.margins.left')}
                      </label>
                      <input
                        type="number"
                        value={currentMargins.left}
                        onChange={(e) =>
                          isSecondary
                            ? setSecondaryMargins((prev) => ({
                                ...prev,
                                left: parseInt(e.target.value)
                              }))
                            : isTertiary
                              ? setTertiaryMargins((prev) => ({
                                  ...prev,
                                  left: parseInt(e.target.value)
                                }))
                              : setMargins((prev) => ({ ...prev, left: parseInt(e.target.value) }))
                        }
                        title={t('printerSettings.margins.leftTitle')}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        {t('printerSettings.margins.right')}
                      </label>
                      <input
                        type="number"
                        value={currentMargins.right}
                        onChange={(e) =>
                          isSecondary
                            ? setSecondaryMargins((prev) => ({
                                ...prev,
                                right: parseInt(e.target.value)
                              }))
                            : isTertiary
                              ? setTertiaryMargins((prev) => ({
                                  ...prev,
                                  right: parseInt(e.target.value)
                                }))
                              : setMargins((prev) => ({ ...prev, right: parseInt(e.target.value) }))
                        }
                        title={t('printerSettings.margins.rightTitle')}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* d) Scale Factor */}
            {/* Scale Factor - Hide when using printer driver defaults */}
            {!currentUseSumatraPDF && !isHotFolder && (
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t('settings.printer.scaleFactor')}
                </label>
                <input
                  type="number"
                  min="10"
                  max="200"
                  step="1"
                  value={currentScaleFactor}
                  onChange={(e) =>
                    isSecondary
                      ? setSecondaryScaleFactor(Math.round(Number(e.target.value)))
                      : isTertiary
                        ? setTertiaryScaleFactor(Math.round(Number(e.target.value)))
                        : setScaleFactor(Math.round(Number(e.target.value)))
                  }
                  title={t('settings.printer.scaleFactor')}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-sm text-gray-500 mt-1">
                  {t('settings.printer.scaleFactorHelp')}
                </span>
              </div>
            )}

            {/* e) Print Background */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id={`printBackground${isSecondary ? '-secondary' : isTertiary ? '-tertiary' : ''}`}
                checked={currentPrintBackground}
                onChange={(e) =>
                  isSecondary
                    ? setSecondaryPrintBackground(e.target.checked)
                    : isTertiary
                      ? setTertiaryPrintBackground(e.target.checked)
                      : setPrintBackground(e.target.checked)
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label
                htmlFor={`printBackground${isSecondary ? '-secondary' : isTertiary ? '-tertiary' : ''}`}
                className="ml-2 block text-sm text-gray-700"
              >
                {t('settings.printer.printBackground')}
              </label>
            </div>

            {/* f) Print Resolution Scale */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                {t('printerSettings.printResolutionScale.title')}
              </label>
              <select
                title={t('printerSettings.printResolutionScale.title')}
                value={currentPrintResolutionScale}
                onChange={(e) => setcurrentPrintResolutionScale(parseFloat(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={0.5}>0.5x</option>
                <option value={1}>1x (Default)</option>
                <option value={2}>2x</option>
                <option value={3}>3x</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t(
                  'printerSettings.printResolutionScale.description',
                  'Adjust the resolution of the final printed image. Higher values result in better quality but larger file sizes.'
                )}
              </p>
            </div>

            {/* Print Position Shift - Collapsible - Only show for versions >= 2.0.2 */}
            {!currentUseSumatraPDF && !isHotFolder && isElectron && getPreloadApi() && supportsSumatraPDF && (
                <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                  <button
                    onClick={() => {
                      if (isSecondary) {
                        setShowSecondaryPositionShift(!showSecondaryPositionShift)
                      } else if (isTertiary) {
                        setShowTertiaryPositionShift(!showTertiaryPositionShift)
                      } else {
                        setShowPositionShift(!showPositionShift)
                      }
                    }}
                    className="w-full flex items-center justify-between text-sm font-medium text-gray-700 mb-0"
                    type="button"
                  >
                    <span>{t('printerSettings.positionShift')}</span>
                    <span>
                      {isSecondary
                        ? showSecondaryPositionShift
                          ? '▼'
                          : '▶'
                        : isTertiary
                          ? showTertiaryPositionShift
                            ? '▼'
                            : '▶'
                          : showPositionShift
                            ? '▼'
                            : '▶'}
                    </span>
                  </button>
                  {(isSecondary
                    ? showSecondaryPositionShift
                    : isTertiary
                      ? showTertiaryPositionShift
                      : showPositionShift) && (
                    <div className="mt-3 space-y-4">
                      <p className="text-xs text-gray-500">
                        {t(
                          'printerSettings.positionShiftHelp',
                          'Fine-tune print alignment. Adjusts @page margins independently from margin settings.'
                        )}
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            {t('printerSettings.positionShiftTop')}
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            value={currentPositionShift.top}
                            onChange={(e) =>
                              isSecondary
                                ? setSecondaryPositionShift((prev) => ({
                                    ...prev,
                                    top: parseFloat(e.target.value) || 0
                                  }))
                                : isTertiary
                                  ? setTertiaryPositionShift((prev) => ({
                                      ...prev,
                                      top: parseFloat(e.target.value) || 0
                                    }))
                                  : setPositionShift((prev) => ({
                                      ...prev,
                                      top: parseFloat(e.target.value) || 0
                                    }))
                            }
                            title={t('printerSettings.positionShiftTopTitle')}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            {t('printerSettings.positionShiftBottom')}
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            value={currentPositionShift.bottom}
                            onChange={(e) =>
                              isSecondary
                                ? setSecondaryPositionShift((prev) => ({
                                    ...prev,
                                    bottom: parseFloat(e.target.value) ?? -0.5
                                  }))
                                : isTertiary
                                  ? setTertiaryPositionShift((prev) => ({
                                      ...prev,
                                      bottom: parseFloat(e.target.value) ?? -0.5
                                    }))
                                  : setPositionShift((prev) => ({
                                      ...prev,
                                      bottom: parseFloat(e.target.value) ?? -0.5
                                    }))
                            }
                            title={t(
                              'printerSettings.positionShiftBottomTitle'
                            )}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            {t('printerSettings.positionShiftLeft')}
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            value={currentPositionShift.left}
                            onChange={(e) =>
                              isSecondary
                                ? setSecondaryPositionShift((prev) => ({
                                    ...prev,
                                    left: parseFloat(e.target.value) || 0
                                  }))
                                : isTertiary
                                  ? setTertiaryPositionShift((prev) => ({
                                      ...prev,
                                      left: parseFloat(e.target.value) || 0
                                    }))
                                  : setPositionShift((prev) => ({
                                      ...prev,
                                      left: parseFloat(e.target.value) || 0
                                    }))
                            }
                            title={t('printerSettings.positionShiftLeftTitle')}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            {t('printerSettings.positionShiftRight')}
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            value={currentPositionShift.right}
                            onChange={(e) =>
                              isSecondary
                                ? setSecondaryPositionShift((prev) => ({
                                    ...prev,
                                    right: parseFloat(e.target.value) || 0
                                  }))
                                : isTertiary
                                  ? setTertiaryPositionShift((prev) => ({
                                      ...prev,
                                      right: parseFloat(e.target.value) || 0
                                    }))
                                  : setPositionShift((prev) => ({
                                      ...prev,
                                      right: parseFloat(e.target.value) || 0
                                    }))
                            }
                            title={t('printerSettings.positionShiftRightTitle')}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* h) Remote Printer Status (only on default tab) */}
            {printerType === 'default' && (
              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h3 className="text-sm font-medium mb-2 text-gray-700">
                  {t('printerSettings.remotePrinterStatus.title')}
                </h3>
                {isTunnelActive ? (
                  <div>
                    <p className="text-sm text-gray-700">
                      {t('printerSettings.remotePrinterStatus.active')}
                    </p>
                    {tunnelUrl && (
                      <p className="text-xs text-gray-600 mt-1">
                        {t('printerSettings.remotePrinterStatus.name')}
                        <span className="font-semibold">
                          {tunnelUrl.match(/https:\/\/([a-zA-Z0-9-]+)\.trycloudflare\.com/)?.[1] ||
                            'N/A'}
                        </span>
                      </p>
                    )}
                    <p className="text-xs text-blue-700 mt-2">
                      {t(
                        'printerSettings.remotePrinterStatus.description',
                        'The remote printer is active. Go to {{appUrl}}/strip to print remotely.',
                        { appUrl: process.env.VITE_APP_URL || 'your app URL' }
                      )}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-red-700">
                    {t(
                      'printerSettings.remotePrinterStatus.inactive',
                      'Status: Inactive or not connected.'
                    )}
                  </p>
                )}
              </div>
            )}

            {printerType === 'default' && (
              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h3 className="text-sm font-medium mb-1 text-gray-800">
                  {t('printerSettings.printResultMinDisplay.title')}
                </h3>
                <p className="text-xs text-gray-600 mb-3">
                  {t('printerSettings.printResultMinDisplay.description')}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS}
                    value={printResultMinDisplaySeconds}
                    onChange={(e) =>
                      setPrintResultMinDisplaySeconds(parseInt(e.target.value, 10) || 0)
                    }
                    className="w-24 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    aria-label={t('printerSettings.printResultMinDisplay.title')}
                  />
                  <span className="text-xs text-gray-600">
                    {t('printerSettings.printResultMinDisplay.suffix', {
                      max: PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS
                    })}
                  </span>
                </div>
              </div>
            )}

            {/* Print Method selector - Only show for versions >= 2.0.2 */}
            {supportsSumatraPDF && (
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <label
                  htmlFor={`print-method-${printerType}`}
                  className="block text-sm font-medium text-gray-800"
                >
                  {t('printerSettings.printMethod.title', 'Print method')}
                </label>
                <select
                  id={`print-method-${printerType}`}
                  value={currentPrintMethod}
                  onChange={(e) => setPrintMethod(e.target.value as PrintMethod, methodSlot)}
                  className="mt-2 w-full p-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="electron">
                    {t('printerSettings.printMethod.electron', 'App Print')}
                  </option>
                  <option value="sumatra">
                    {t('printerSettings.printMethod.sumatra', 'Default Driver')}
                  </option>
                  {supportsHotFolder && (
                    <option value="hotFolder">
                      {t('printerSettings.printMethod.hotFolder', 'Hot Folder')}
                    </option>
                  )}
                </select>

                {/* Contextual help for the selected method */}
                <p className="text-xs text-gray-500 mt-2">{printMethodHelp[currentPrintMethod]}</p>

                {/* Hot-folder configuration */}
                {isHotFolder && (
                  <div className="mt-3 space-y-2 border-t border-gray-200 pt-3">
                    <label
                      htmlFor={`hot-folder-path-${printerType}`}
                      className="block text-xs font-medium text-gray-700"
                    >
                      {t('printerSettings.printMethod.hotFolderPath', 'Watched folder')}
                    </label>
                    <div className="flex gap-2">
                      <input
                        id={`hot-folder-path-${printerType}`}
                        type="text"
                        value={currentHotFolderConfig?.folderPath ?? ''}
                        onChange={(e) =>
                          setHotFolderConfig({ folderPath: e.target.value }, methodSlot)
                        }
                        placeholder={t(
                          'printerSettings.printMethod.hotFolderPlaceholder',
                          'e.g. C:\\DNP\\HotFolder\\6x20'
                        )}
                        className="flex-1 p-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                      />
                      <Button
                        text={t('printerSettings.printMethod.browse', 'Browse')}
                        variant="secondary"
                        onClick={async () => {
                          const fs = getPreloadApi()
                          if (!fs?.showOpenDirectoryDialog) return
                          const picked = await fs.showOpenDirectoryDialog()
                          if (picked) {
                            setHotFolderConfig({ folderPath: picked }, methodSlot)
                          }
                        }}
                        className="!px-3 !py-1 !text-xs whitespace-nowrap"
                      />
                    </div>
                    {!currentHotFolderConfig?.folderPath?.trim() && (
                      <p className="text-[11px] text-amber-600">
                        {t(
                          'printerSettings.printMethod.hotFolderRequired',
                          'Select a watched folder, otherwise prints with this method will fail.'
                        )}
                      </p>
                    )}
                    <p className="text-[11px] text-gray-500">
                      {t(
                        'printerSettings.printMethod.hotFolderHelp',
                        'Point this at a folder monitored by your print software (e.g. DNP Hot Folder Print), mapped to the desired output size. The app drops one image file per copy; the print software handles the size and cutting.'
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* i) C# Printing Toggle (only on default tab) */}
            {printerType === 'default' && isElectron && process.env.NODE_ENV === 'development' && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-blue-800">
                      {t(
                        'printerSettings.csharpPrinting.title',
                        'C# Direct Printing (Experimental)'
                      )}
                    </h4>
                    <p className="text-xs text-blue-600 mt-1">
                      {t(
                        'printerSettings.csharpPrinting.description',
                        'Use .NET printing for better reliability with certain printers'
                      )}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useCSharpPrinting}
                      onChange={(e) => setUseCSharpPrinting(e.target.checked)}
                      className="sr-only peer"
                      aria-label={t('printerSettings.csharpPrinting.toggle')}
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                  </label>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  const mobileSheet = capabilities.isMobile || isViewportCompact

  return (
    <div
      className={`fixed inset-0 z-[200] bg-black/50 ${
        mobileSheet ? 'flex flex-col justify-end p-0' : 'flex items-center justify-center p-4 sm:p-6'
      }`}
    >
      <div
        className={`flex w-full flex-col bg-white shadow-2xl ${
          mobileSheet
            ? 'mx-0 h-[95dvh] max-h-[95dvh] rounded-t-2xl'
            : 'mx-auto max-w-6xl rounded-2xl h-[90vh]'
        }`}
      >
        <div className="flex h-full min-h-0 flex-col md:flex-row">
          {/* Settings Panel */}
          <div
            className={`w-full overflow-y-auto md:w-1/2 md:min-h-0 ${
              mobileSheet ? 'max-h-[36vh] shrink-0 border-b border-gray-200 md:max-h-none md:shrink md:border-b-0' : 'min-h-0'
            } ${printerSheetDense ? 'p-2.5 [&_label]:mb-0.5 [&_label]:text-[11px] [&_input]:py-1.5 [&_input]:text-xs [&_select]:py-1.5 [&_select]:text-xs [&_.space-y-6]:!space-y-3 [&_.mb-6]:!mb-3 [&_.text-sm]:!text-xs' : mobileSheet ? 'p-3 md:p-4' : 'p-4 md:p-8'}`}
          >
            <h2
              className={`font-bold ${printerSheetDense ? 'mb-2 text-base' : mobileSheet ? 'mb-3 text-lg md:mb-6 md:text-xl' : 'mb-4 text-2xl md:mb-6'}`}
            >
              {t('settings.printer.settings')}
            </h2>

            {/* Tabs */}
            <div className={`flex border-b border-gray-200 ${printerSheetDense ? 'mb-3' : 'mb-6'}`}>
              <button
                type="button"
                onClick={() => setActiveTab('default')}
                className={`font-medium ${printerSheetDense ? 'px-2 py-1 text-[11px]' : 'px-4 py-2 text-sm'} ${
                  activeTab === 'default'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('printerSettings.defaultPrinter')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('secondary')}
                className={`font-medium ${printerSheetDense ? 'px-2 py-1 text-[11px]' : 'px-4 py-2 text-sm'} ${
                  activeTab === 'secondary'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('printerSettings.secondaryPrinter')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('tertiary')}
                className={`font-medium ${printerSheetDense ? 'px-2 py-1 text-[11px]' : 'px-4 py-2 text-sm'} ${
                  activeTab === 'tertiary'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('printerSettings.tertiaryPrinter')}
              </button>
            </div>

            {/* Printer Purpose Dropdown - Only for secondary and tertiary */}
            {(activeTab === 'secondary' || activeTab === 'tertiary') && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  {t('printerSettings.printerPurpose')}
                </label>
                <select
                  value={activeTab === 'secondary' ? secondaryPurpose : tertiaryPurpose}
                  onChange={(e) => {
                    const purpose = e.target.value as 'special-frames' | 'parallel'
                    if (activeTab === 'secondary') {
                      setSecondaryPurpose(purpose)
                    } else {
                      setTertiaryPurpose(purpose)
                    }
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  title={t('printerSettings.printerPurpose')}
                >
                  <option value="special-frames">
                    {t('printerSettings.specialFramesPrint')}
                  </option>
                  <option value="parallel">
                    {t('printerSettings.parallelPrinting')}
                  </option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {activeTab === 'secondary'
                    ? t(
                        'printerSettings.printerPurposeHelpSecondary',
                        'Select the purpose for the secondary printer: special frames for frame-based routing, or parallel for round-robin distribution.'
                      )
                    : t(
                        'printerSettings.printerPurposeHelpTertiary',
                        'Select the purpose for the tertiary printer: special frames for frame-based routing, or parallel for round-robin distribution.'
                      )}
                </p>
              </div>
            )}

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    {t(
                      activeTab === 'default'
                        ? 'printerSettings.testPrintInfo.title'
                        : activeTab === 'secondary'
                          ? 'printerSettings.secondaryPrinterInfo.title'
                          : 'printerSettings.tertiaryPrinterInfo.title',
                      activeTab === 'default'
                        ? 'Test Print Configuration'
                        : activeTab === 'secondary'
                          ? 'Secondary Printer Configuration'
                          : 'Tertiary Printer Configuration'
                    )}
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      {t(
                        activeTab === 'default'
                          ? 'printerSettings.testPrintInfo.description'
                          : activeTab === 'secondary'
                            ? 'printerSettings.secondaryPrinterInfo.description'
                            : 'printerSettings.tertiaryPrinterInfo.description',
                        activeTab === 'default'
                          ? 'Use this section to test print settings and save your preferred configuration. These settings will be used for automated printing during photo sessions.'
                          : activeTab === 'secondary'
                            ? 'Configure a secondary printer for specific frames. You can select which frames will be printed using this printer. This is useful for printing different frame types on different printers.'
                            : 'Configure a tertiary printer for specific frames. You can select which frames will be printed using this printer. This is useful for printing different frame types on different printers.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {renderPrinterSettings(activeTab)}
          </div>

          {/* Preview Panel — larger share of height on portrait mobile sheet */}
          <div
            className={`flex h-full min-h-0 w-full flex-1 flex-col overflow-y-auto border-t border-gray-200 md:w-1/2 md:flex-none md:border-t-0 md:border-l ${
              mobileSheet ? 'min-h-[50vh]' : ''
            }`}
          >
            <div className={printerSheetDense ? 'flex min-h-0 flex-1 flex-col p-2.5' : mobileSheet ? 'flex min-h-0 flex-1 flex-col p-3 md:p-6' : 'p-8'}>
              <h3 className={`font-medium ${printerSheetDense ? 'mb-1.5 text-sm' : mobileSheet ? 'mb-2 text-base md:mb-4 md:text-lg' : 'mb-4 text-lg'}`}>
                {t('printerSettings.preview')}
              </h3>
              <div
                className={`border border-gray-200 rounded-lg bg-gray-50 p-2 md:p-4 ${
                  mobileSheet
                    ? 'min-h-[220px] flex-1 h-[min(52vh,480px)] md:h-[calc(60vh-8rem)] md:min-h-0 md:flex-none'
                    : 'h-[calc(60vh-8rem)]'
                }`}
              >
                {previewImage ? (
                  <img
                    crossOrigin="anonymous"
                    src={previewImage}
                    alt="Print Preview"
                    className="object-contain h-full w-full"
                    style={{
                      aspectRatio: `${
                        activeTab === 'default'
                          ? tempSettings.pageSize.width
                          : activeTab === 'secondary'
                            ? tempSecondarySettings.pageSize.width
                            : tempTertiarySettings.pageSize.width
                      } / ${
                        activeTab === 'default'
                          ? tempSettings.pageSize.height
                          : activeTab === 'secondary'
                            ? tempSecondarySettings.pageSize.height
                            : tempTertiarySettings.pageSize.height
                      }`
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">
                    {t('printerSettings.noPreview')}
                  </div>
                )}
              </div>

              {/* Frame Test Section */}
              <div className={printerSheetDense ? 'mt-3' : mobileSheet ? 'mt-4 md:mt-8' : 'mt-8'}>
                <h3
                  className={`font-medium ${printerSheetDense ? 'mb-2 text-sm' : mobileSheet ? 'mb-2 text-base md:mb-4 md:text-lg' : 'mb-4 text-lg'}`}
                >
                  {t('printerSettings.frameTest.title')}
                </h3>
                <div className={printerSheetDense ? 'space-y-2' : 'space-y-4'}>
                  {/* Frame Selection Dropdown */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      {t('frame.framesListbox')}
                    </label>
                    <select
                      value={selectedFrameId}
                      onChange={(e) => setSelectedFrameId(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      title={t('frame.framesListbox')}
                    >
                      <option value="">{t('frame.framesListbox')}</option>
                      {frames.map((frame) => (
                        <option key={frame._id} value={frame._id}>
                          {frame.name || `Frame ${frame._id}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">{t('common.or')}</span>
                    </div>
                  </div>

                  {/* Upload Section */}
                  <div
                    className={`border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                      printerSheetDense ? 'p-4' : mobileSheet ? 'p-5 md:p-8' : 'p-8'
                    } ${
                      isDragging
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-blue-500'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => document.getElementById('frame-upload')?.click()}
                  >
                    <input
                      id="frame-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e): void => {
                        const file = e.target.files?.[0]
                        if (file) {
                          handleFileUpload(file)
                          setSelectedFrameId('') // Clear frame selection when uploading
                        }
                      }}
                      className="hidden"
                      title={t('printerSettings.upload')}
                    />
                    <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">
                      {t(
                        'printerSettings.frameTest.dragAndDrop',
                        'Drag and drop an image here, or click to select'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`shrink-0 border-t border-gray-200 bg-white ${printerSheetDense ? 'px-2.5 py-2' : mobileSheet ? 'px-3 py-3' : 'rounded-b-2xl p-4'}`}
        >
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
            <Button
              text={t('common.cancel')}
              variant="secondary"
              onClick={onClose}
              className={printerSheetDense || mobileSheet ? '!px-3 !py-1.5 !text-xs' : '!px-6 !py-2 !text-base'}
            />
            {isNativeShell && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">
                  {t('printerSettings.copies')}
                </label>
                <input
                  type="number"
                  min="1"
                  value={copies}
                  onChange={(e) => setCopies(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-16 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  title={t('printerSettings.copiesTitle')}
                />
              </div>
            )}
            <Button
              text={
                <>
                  <FaPrint className="mr-1 inline sm:mr-2" />
                  {t('printerSettings.preview')}
                </>
              }
              variant="secondary"
              onClick={handlePreview}
              className={mobileSheet ? '!px-3 !py-1.5 !text-xs' : '!px-6 !py-2 !text-base'}
              disabled={
                !previewImage ||
                (isNativeShell && activeTab === 'default' && !selectedPrinter) ||
                (isNativeShell && activeTab === 'secondary' && !selectedSecondaryPrinter) ||
                (isNativeShell && activeTab === 'tertiary' && !selectedTertiaryPrinter)
              }
            />
            <Button
              text={t('common.save')}
              onClick={() => {
                saveAllTabs()
                onClose()
              }}
              className={mobileSheet ? '!px-3 !py-1.5 !text-xs' : '!px-6 !py-2 !text-base'}
            />
          </div>
        </div>
      </div>

      {/* Frame Selection Modal */}
      {/* Key prop ensures complete remount when switching between secondary and tertiary */}
      <FrameSelectionModal
        key={`frame-selection-${frameSelectionPrinterType}`}
        isOpen={isFrameSelectionOpen}
        onClose={() => setIsFrameSelectionOpen(false)}
        selectedFrames={
          frameSelectionPrinterType === 'secondary'
            ? [...(secondaryPrinter?.frameIds || [])]
            : [...(tertiaryPrinter?.frameIds || [])]
        }
        onFramesSelected={(frameIds) => {
          // Create a new array to ensure we don't share references
          const newFrameIds = [...frameIds]

          if (frameSelectionPrinterType === 'secondary') {
            setSecondaryPrinter({
              printerName: selectedSecondaryPrinter || secondaryPrinter?.printerName || '',
              settings: secondaryPrinter?.settings || {
                ...tempSecondarySettings,
                margins: {
                  marginType: secondaryMarginType,
                  ...(secondaryMarginType === 'custom' ? secondaryMargins : {})
                },
                dpi: secondaryDpi,
                scaleFactor: secondaryScaleFactor,
                printBackground: secondaryPrintBackground,
                printResolutionScale: secondaryPrintResolutionScale,
                printPositionShift: secondaryPositionShift
              },
              frameIds: newFrameIds,
              purpose: secondaryPurpose
            })
          } else {
            setTertiaryPrinter({
              printerName: selectedTertiaryPrinter || tertiaryPrinter?.printerName || '',
              settings: tertiaryPrinter?.settings || {
                ...tempTertiarySettings,
                margins: {
                  marginType: tertiaryMarginType,
                  ...(tertiaryMarginType === 'custom' ? tertiaryMargins : {})
                },
                dpi: tertiaryDpi,
                scaleFactor: tertiaryScaleFactor,
                printBackground: tertiaryPrintBackground,
                printResolutionScale: tertiaryPrintResolutionScale,
                printPositionShift: tertiaryPositionShift
              },
              frameIds: newFrameIds,
              purpose: tertiaryPurpose
            })
          }
        }}
        printerType={frameSelectionPrinterType}
      />
    </div>
  )
}
